import { memo } from 'react'
import { Box, Stack, Center, Text, Progress } from 'components'
import { IconSparkles } from '@tabler/icons-react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { useTranslation } from 'react-i18next'

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`

const AILoadingIcon = styled.div`
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(139, 92, 246, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #8b5cf6;
  }
`

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`

const AnimatedBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(139, 92, 246, 0.05) 0%,
    rgba(139, 92, 246, 0.1) 50%,
    rgba(139, 92, 246, 0.05) 100%
  );
  background-size: 200% 200%;
  animation: ${gradientAnimation} 4s ease infinite;
  border-radius: 8px;
  pointer-events: none;
`

interface AILoadingStateProps {
  message: string
  progress: number
}

export const AILoadingState = memo(function AILoadingState({ message, progress }: AILoadingStateProps) {
  const { t } = useTranslation()
  
  return (
    <Box style={{ position: 'relative' }}>
      <AnimatedBackground />
      <Stack spacing='xl' p='xl' align='center' style={{ position: 'relative', zIndex: 1 }}>
        <Center>
          <AILoadingIcon>
            <IconSparkles size={40} />
          </AILoadingIcon>
        </Center>

        <Stack spacing='md' align='center' style={{ width: '100%' }}>
          <Text size='lg' weight={600} align='center'>
            {message || t('new_project_page.ai_processing')}
          </Text>
          <Text size='sm' color='dimmed' align='center'>
            {t('new_project_page.ai_please_wait')}
          </Text>

          <Box style={{ width: '100%', maxWidth: 400 }}>
            <Progress
              value={progress}
              size='sm'
              color='grape'
              animate
              striped
              radius='xl'
            />
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
})